<template>
    <div ref="tooltipContainer" class="relative inline-block">
        <div ref="trigger" class="cursor-pointer" @click="handleTriggerClick">
            <slot></slot>
        </div>

        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="isVisible"
                ref="tooltip"
                :class="[
                    'fixed z-50 rounded-lg max-w-90vw md:max-w-sm',
                    shadow ? 'shadow-lg' : 'shadow-md',
                    borderThick
                        ? 'border-2 border-theme-border'
                        : 'border border-theme-border',
                    backgroundColor
                ]"
                :style="tooltipStyle"
                @click.stop
            >
                <div
                    v-if="arrow"
                    :class="['absolute w-3 h-3 rotate-45', backgroundColor]"
                    :style="arrowStyle"
                ></div>

                <div class="relative z-10 px-1 whitespace-normal text-sm w-max">
                    <slot name="tooltip"></slot>
                </div>
            </div>
        </transition>

        <transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isVisible"
                class="fixed inset-0 z-40"
                @click="hide"
            ></div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "DropdownPopover",
    props: {
        value: { type: Boolean, default: false },
        position: { type: String, default: "bottom" },
        shadow: { type: Boolean, default: false },
        borderThick: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        arrow: { type: Boolean, default: true },
        color: { type: String, default: "" },
        offset: { type: Number, default: 12 }
    },
    data() {
        return {
            isVisible: this.value,
            tooltipStyle: {},
            arrowStyle: {}
        };
    },
    computed: {
        backgroundColor() {
            const colorMap = {
                white: "bg-theme-card text-theme-base",
                black: "bg-black text-white",
                primary: "bg-theme-primary text-white",
                success: "bg-theme-success text-white",
                danger: "bg-theme-danger text-white",
                warning: "bg-theme-warning text-white"
            };
            return colorMap[this.color] || "bg-theme-canvas";
        }
    },
    watch: {
        value(newVal) {
            this.isVisible = newVal;
        },
        isVisible(newVal) {
            this.$emit("input", newVal);
            if (newVal) this.calculatePosition();
        }
    },
    mounted() {
        this.calculatePosition();
        window.addEventListener("resize", this.handleResize);
        window.addEventListener("scroll", this.handleScroll, true);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        window.removeEventListener("scroll", this.handleScroll, true);
    },
    methods: {
        handleResize() {
            if (this.isVisible) this.calculatePosition();
        },
        handleScroll() {
            if (this.isVisible) this.calculatePosition();
        },
        // eslint-disable-next-line no-unused-vars
        handleTriggerClick(__) {
            if (this.disabled) return;

            if (this.isVisible) {
                this.hide();
            } else {
                this.show();
            }
        },
        show() {
            this.isVisible = true;
        },
        hide() {
            this.isVisible = false;
        },
        calculatePosition() {
            this.$nextTick(() => {
                const trigger = this.$refs.trigger;
                const tooltip = this.$refs.tooltip;
                if (!trigger || !tooltip) return;

                const triggerRect = trigger.getBoundingClientRect();
                const tWidth = trigger.offsetWidth;
                const tHeight = trigger.offsetHeight;
                const tooltipWidth = tooltip.offsetWidth;
                const tooltipHeight = tooltip.offsetHeight;

                let top = 0;
                let left = 0;
                let arrowPos = {
                    top: "auto",
                    left: "auto",
                    right: "auto",
                    bottom: "auto"
                };

                switch (this.position) {
                    case "top":
                        top = -(tooltipHeight + this.offset);
                        left = tWidth / 2 - tooltipWidth / 2;
                        arrowPos = {
                            bottom: "-6px",
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            borderRight: "1px solid currentColor",
                            borderBottom: "1px solid currentColor"
                        };
                        break;
                    case "bottom":
                        top = tHeight + this.offset;
                        left = tWidth / 2 - tooltipWidth / 2;
                        arrowPos = {
                            top: "-6px",
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            borderLeft: "1px solid currentColor",
                            borderTop: "1px solid currentColor"
                        };
                        break;
                    case "left":
                        top = tHeight / 2 - tooltipHeight / 2;
                        left = -(tooltipWidth + this.offset);
                        arrowPos = {
                            right: "-6px",
                            top: "50%",
                            transform: "translateY(-50%) rotate(45deg)",
                            borderRight: "1px solid currentColor",
                            borderTop: "1px solid currentColor"
                        };
                        break;
                    case "right":
                        top = tHeight / 2 - tooltipHeight / 2;
                        left = tWidth + this.offset;
                        arrowPos = {
                            left: "-6px",
                            top: "50%",
                            transform: "translateY(-50%) rotate(45deg)",
                            borderLeft: "1px solid currentColor",
                            borderBottom: "1px solid currentColor"
                        };
                        break;
                }

                top += triggerRect.top;
                left += triggerRect.left;

                // Clamping lateral
                const padding = 12;
                const leftOnScreen = left;
                const rightOnScreen = leftOnScreen + tooltipWidth;

                let correctionX = 0;
                if (leftOnScreen < padding)
                    correctionX = padding - leftOnScreen;
                else if (rightOnScreen > window.innerWidth - padding)
                    correctionX = window.innerWidth - padding - rightOnScreen;

                left += correctionX;
                if (["top", "bottom"].includes(this.position)) {
                    arrowPos.left = `calc(50% - ${correctionX}px)`;
                }

                this.tooltipStyle = { top: `${top}px`, left: `${left}px` };
                this.arrowStyle = {
                    ...arrowPos,
                    borderColor: this.borderThick ? "#e5e7eb" : "#f3f4f6"
                };
            });
        }
    }
};
</script>
